import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { socialProjectsApi } from "../../api/endpoints";
import type { SocialProjectCreate, SocialProjectUpdate } from "@endeks/shared";

// Social Projects
export function useSocialProject(id: string) {
  return useQuery({
    queryKey: ["social-projects", id],
    queryFn: () => socialProjectsApi.get(id).then((r) => r.data),
    enabled: !!id,
  });
}

export function useSocialProjects() {
  return useQuery({
    queryKey: ["social-projects"],
    queryFn: () => socialProjectsApi.list().then((r) => r.data),
  });
}

export function useCreateSocialProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: SocialProjectCreate) => socialProjectsApi.create(data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["social-projects"] }),
  });
}

export function useUpdateSocialProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: SocialProjectUpdate) =>
      socialProjectsApi.update(id, data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["social-projects"] }),
  });
}

export function useDeleteSocialProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => socialProjectsApi.delete(id).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["social-projects"] }),
  });
}

export function useReorderSocialProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, order }: { id: string; order: number }) =>
      socialProjectsApi.reorder(id, order).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["social-projects"] }),
  });
}

export function useReorderSocialProjects() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (items: { id: string; order: number }[]) =>
      socialProjectsApi.reorderAll(items).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["social-projects"] }),
  });
}
